import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	MotifButton,
	MotifProgressLoader,
	MotifErrorMessage,
} from "@ey-xd/motif-react";
import Tree from "@/components/Tree/Tree";
import { useForm, FormProvider } from "react-hook-form";
import { useGetCreditorListAttributeQuery } from "@/services/advanceSearch/advanceSearchReducer";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { processTreeRows } from "@/utility/common";
import { customPaginationPageSize } from "@/components/Table/CustomPaginationConstants";
import {
	APPLY_BUTTON_VARIANT,
	CANCEL_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { useDispatch } from "react-redux";

interface AdvanceSearchProps {
	setShowAdvanceSearch: (show: boolean) => void;
	setClearSearch: (show: boolean) => void;
	advanceSearchPayload: any;
	setAdvanceSearchPayload: (payload: any) => void;
	setIsAdvanceSearchActive: (show: boolean) => void;
	setPaginationObj?: (paginationObj: any) => void;
	handleResetPaginationandQuickSearch?: () => void;
}

const AdvanceSearch = ({
	setShowAdvanceSearch,
	setClearSearch,
	advanceSearchPayload,
	setAdvanceSearchPayload,
	setIsAdvanceSearchActive,
	setPaginationObj,
	handleResetPaginationandQuickSearch,
}: AdvanceSearchProps) => {
	const { t } = useTranslation();
	const { caseId } = useParams();
	const dispatch = useDispatch();
	const paginationPageSize: number = customPaginationPageSize;
	const [isApplyLoading, setIsApplyLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [attributes, setAttributes] = useState([]);
	const methods = useForm({
		defaultValues: {
			treeRows: [],
			logicalOperator: "0",
		},
	});

	const {
		handleSubmit,
		formState: { errors },
		reset,
	} = methods;
	const errorList = Object.keys(errors);
	const {
		data: attributesData,
		isLoading,
		error,
	} = useGetCreditorListAttributeQuery(caseId, { skip: !caseId });
	const watchTreeRows = methods.watch("treeRows");
	useEffect(() => {
	  if (errorMessage) {
		if (watchTreeRows.length >= 0) {
		  setErrorMessage("");
		}
	  }
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchTreeRows]);
	useEffect(() => {
		if (attributesData) {
			setAttributes(attributesData);
		}
	}, [attributesData]);

	useEffect(() => {
		if (
			attributes.length > 0 &&
			advanceSearchPayload &&
			Object.keys(advanceSearchPayload).length > 0
		) {
			const transformRules = (rules: any[]) => {
				return rules.map((rule: any) => ({
					...rule,
					attribute: `${rule.entityName}.${rule.propertyName}`,
					type: "row",
				}));
			};

			const transformGroups = (groups: any[]): any => {
				return groups.map((group: any) => ({
					...group,
					logicalOperator: group.logicalOperator.toString(),
					treeRows: [...transformRules(group.rules)],
					type: "group",
				}));
			};
			const transformedData = {
				treeRows: [
					...transformRules(advanceSearchPayload.rules || []),
					...(Array.isArray(advanceSearchPayload.groups)
						? transformGroups(advanceSearchPayload.groups)
						: []),
				],
				logicalOperator: advanceSearchPayload.LogicalOperator.toString(),
			};
			reset(transformedData);
		}
	}, [advanceSearchPayload, reset, attributes]);

	if (isLoading) {
		return <MotifProgressLoader show variant="default" />;
	}
	if (error) {
		return (
			<MotifErrorMessage>
				{t("global.globalNotifications.wentWrong")}
			</MotifErrorMessage>
		);
	}
	const onSubmit = async (data: any) => {
		const { rules, groups } = processTreeRows(data.treeRows, caseId);
		if (watchTreeRows.length === 0) {
			setErrorMessage(t("pages.configuration.scheme.rule.noConditionAdded"));
			return;
		  } else {
			setErrorMessage("");
		  }
		const payload = {
			CaseId: caseId,
			rules,
			groups: groups.length > 0 ? groups : null,
			LogicalOperator: parseInt(data.logicalOperator),
		};
		if (setPaginationObj) {
			setPaginationObj({
				PageNumber: 1,
				PageSize: paginationPageSize,
				SearchText: "",
			});
		}
		if (handleResetPaginationandQuickSearch) {
			handleResetPaginationandQuickSearch();
		}
		setErrorMessage("");
		setIsApplyLoading(true);
		setAdvanceSearchPayload(payload);
		dispatch(
			  setComponentState1({
				entityName: "creditorListTabState",
				entityState: { advanceSearchPayload: payload,isAdvanceSearchActive: true ,clearSearch: true },
			  })
			);
		setShowAdvanceSearch(false);
		setClearSearch(true);
		setIsAdvanceSearchActive(true);
	};

	return (
		<div className="claims-basic-search-container">
			{errorMessage && (
				<NotificationGrid show={true} message={errorMessage} type="error" onClose={()=>setErrorMessage("")} />
			)}
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Tree attributes={attributes} />
					<div className="error-container">
						<MotifErrorMessage>
							{errorList.length
								? t("pages.configuration.scheme.rule.validationError")
								: ""}
						</MotifErrorMessage>
					</div>
					<div className="basic-search-buttons">
						<MotifButton
							size="small"
							type="button"
							variant={CANCEL_BUTTON_VARIANT}
							onClick={() => {
								setShowAdvanceSearch(false);
							}}
						>
							{t("pages.claims.advancedSearch.cancel")}
						</MotifButton>

						<MotifButton
							size="small"
							variant={APPLY_BUTTON_VARIANT}
							type={isApplyLoading ? "loader" : "submit"}
							onClick={handleSubmit(onSubmit)}
						>
							{t("pages.claims.advancedSearch.apply")}
						</MotifButton>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
export default AdvanceSearch;
